import { useEffect, useState, useCallback } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";

export function useSC(path) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(() => {
    setLoading(true);
    api.get(`/admin/showcase${path}`).then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [path]);
  useEffect(() => { load(); }, [load]);
  return { data, loading };
}

export const money = (n) => `$${Math.round(n || 0).toLocaleString("en-US")}`;
export const num = (n) => (n || 0).toLocaleString("en-US");
export const fmtDate = (d) => (d ? new Date(d + "T00:00:00").toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—");
export const monthLabel = (m) => new Date(m + "-01T00:00:00").toLocaleDateString("en-GB", { month: "short", year: "2-digit" });

const TONES = {
  green: ["#DEF7DA", "#2C633D"], yellow: ["#FFF3C4", "#8B6D00"], blue: ["#E3ECFB", "#2F4F8F"],
  red: ["#F7E0E0", "#8F2929"], grey: ["#ECECEF", "#4A4A5A"],
};
export const SCEN_TONE = {
  active: "green", delivered: "green", following: "green", due_revision: "yellow", awaiting_approval: "yellow",
  pending_delivery: "yellow", awaiting_consult: "blue", renewing: "blue", completed: "grey", discontinued: "red", onboarding: "blue",
};

export function Pill({ tone = "grey", children }) {
  const [bg, fg] = TONES[tone];
  return <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full whitespace-nowrap" style={{ background: bg, color: fg }}>{children}</span>;
}

export function Kpi({ label, value, sub, testid, accent = "var(--primary)" }) {
  return (
    <div data-testid={testid} className="card-soft p-4">
      <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: "var(--text-secondary)" }}>{label}</div>
      <div className="font-display text-2xl md:text-3xl mt-1" style={{ color: accent }}>{value}</div>
      {sub && <div className="text-xs mt-1" style={{ color: "var(--text-secondary)" }}>{sub}</div>}
    </div>
  );
}

export function Card({ title, right, children, className = "" }) {
  return (
    <div className={`card-soft p-5 ${className}`}>
      {(title || right) && (
        <div className="flex items-center justify-between gap-2 mb-3">
          <div className="font-display text-lg" style={{ color: "var(--primary)" }}>{title}</div>
          {right}
        </div>
      )}
      {children}
    </div>
  );
}

export function Pager({ data, page, setPage, testid }) {
  if (!data) return null;
  return (
    <div className="flex items-center justify-between text-xs pt-3" style={{ color: "var(--text-secondary)" }}>
      <span>{num(data.total)} records · page {data.page} of {num(data.pages)}</span>
      <div className="flex gap-2">
        <button data-testid={`${testid}-prev`} disabled={page <= 1} onClick={() => setPage(page - 1)} className="p-1 rounded-full border disabled:opacity-30" style={{ borderColor: "var(--border-strong)" }}><ChevronLeft size={14} /></button>
        <button data-testid={`${testid}-next`} disabled={page >= data.pages} onClick={() => setPage(page + 1)} className="p-1 rounded-full border disabled:opacity-30" style={{ borderColor: "var(--border-strong)" }}><ChevronRight size={14} /></button>
      </div>
    </div>
  );
}

export function Table({ head, children }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead><tr className="text-left">{head.map((h) => <th key={h} className="p-2 text-[10px] uppercase tracking-widest font-bold whitespace-nowrap" style={{ color: "var(--text-secondary)" }}>{h}</th>)}</tr></thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

export const Td = ({ children, className = "" }) => <td className={`p-2 border-t whitespace-nowrap ${className}`} style={{ borderColor: "var(--border-subtle)" }}>{children}</td>;

export function Drawer({ onClose, children, testid }) {
  return (
    <div className="fixed inset-0 z-50 flex justify-end" style={{ background: "rgba(20,20,43,0.35)" }} onClick={onClose}>
      <div data-testid={testid} className="h-full w-full max-w-4xl overflow-y-auto p-5 md:p-8" style={{ background: "var(--bg-paper)" }} onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-end items-center mb-4">
          <button data-testid={`${testid}-close`} onClick={onClose} className="p-2 rounded-full" style={{ background: "var(--bg)" }}><X size={16} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

export const DemoTag = () => <span className="text-[10px] font-bold tracking-widest px-2 py-1 rounded" style={{ background: "var(--red)", color: "#fff" }}>DEMO DATA</span>;

export const Loading = () => <div className="p-10 text-center text-sm" style={{ color: "var(--text-secondary)" }}>Loading…</div>;
